import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { NotificationDto, NotificationListResultDto } from "@/api/notifications";

export const notificationQueryKeys = {
  all: ["notifications"] as const,
  lists: () => ["notifications", "list"] as const,
  list: (unreadOnly: boolean) => ["notifications", "list", unreadOnly] as const,
  unreadCount: ["notifications", "unread-count"] as const,
};

type NotificationListCache = InfiniteData<NotificationListResultDto, number | undefined>;

function mapListCache(cache: NotificationListCache | undefined, transform: (items: NotificationDto[]) => NotificationDto[]): NotificationListCache | undefined {
  if (!cache) {
    return cache;
  }
  return {
    ...cache,
    pages: cache.pages.map((page) => ({ ...page, items: transform(page.items) })),
  };
}

export function mergeIncomingNotifications(queryClient: QueryClient, items: NotificationDto[]) {
  if (items.length === 0) {
    return;
  }

  for (const unreadOnly of [false, true]) {
    const incoming = unreadOnly ? items.filter((item) => item.readTime === null) : items;
    if (incoming.length === 0) {
      continue;
    }

    queryClient.setQueryData<NotificationListCache>(notificationQueryKeys.list(unreadOnly), (cache) => {
      if (!cache?.pages[0]) {
        return cache;
      }
      const known = new Set(cache.pages.flatMap((page) => page.items.map((item) => item.cursor)));
      const additions = incoming.filter((item) => !known.has(item.cursor));
      if (additions.length === 0) {
        return cache;
      }
      const firstPage = cache.pages[0];
      return {
        ...cache,
        pages: [
          {
            ...firstPage,
            items: [...additions, ...firstPage.items].sort((left, right) => right.cursor - left.cursor),
          },
          ...cache.pages.slice(1),
        ],
      };
    });
  }
}

export function markNotificationReadInCache(queryClient: QueryClient, cursor: number, readTime: string) {
  queryClient.setQueryData<NotificationListCache>(notificationQueryKeys.list(false), (cache) =>
    mapListCache(cache, (items) => items.map((item) => (item.cursor === cursor ? { ...item, readTime: item.readTime ?? readTime } : item))),
  );
  queryClient.setQueryData<NotificationListCache>(notificationQueryKeys.list(true), (cache) =>
    mapListCache(cache, (items) => items.filter((item) => item.cursor !== cursor)),
  );
}

export function markAllNotificationsReadInCache(queryClient: QueryClient, throughCursor: number, readTime: string) {
  queryClient.setQueryData<NotificationListCache>(notificationQueryKeys.list(false), (cache) =>
    mapListCache(cache, (items) => items.map((item) => (item.cursor <= throughCursor && item.readTime === null ? { ...item, readTime } : item))),
  );
  queryClient.setQueryData<NotificationListCache>(notificationQueryKeys.list(true), (cache) =>
    mapListCache(cache, (items) => items.filter((item) => item.cursor > throughCursor)),
  );
}
